import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Avatar,
  Box,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { pb, notifyAvatarChange } from "../services/pocketbase";

const Account = () => {
  const navigate = useNavigate();
  const user = pb.authStore.model;
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [avatar, setAvatar] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    if (!pb.authStore.isValid) {
      navigate("/login");
    }
  }, [navigate]);

  const [formData, setFormData] = useState({
    username: user?.username || "",
    email: user?.email || "",
    discord_id: user?.discord_id || "",
    name: user?.name || "",
  });

  // Function to format Discord data for display
  const formatDiscordData = () => {
    if (!user?.discord_data) return null;

    try {
      const discordData =
        typeof user.discord_data === "string"
          ? JSON.parse(user.discord_data)
          : user.discord_data;

      return (
        <Box
          sx={{
            mt: 2,
            p: 2,
            bgcolor: "rgba(114, 137, 218, 0.1)",
            borderRadius: 1,
            border: "1px solid rgba(114, 137, 218, 0.3)",
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            Discord Data
          </Typography>
          <Box
            sx={{
              maxHeight: "300px",
              overflowY: "auto",
              border: "1px solid rgba(114, 137, 218, 0.2)",
              borderRadius: 1,
              p: 1,
            }}
          >
            <pre
              style={{
                overflowX: "auto",
                whiteSpace: "pre-wrap",
                wordWrap: "break-word",
                fontSize: "0.85rem",
                margin: 0,
              }}
            >
              {JSON.stringify(discordData, null, 2)}
            </pre>
          </Box>
        </Box>
      );
    } catch (err) {
      console.error("Error parsing Discord data:", err);
      return (
        <Alert severity="error" sx={{ mt: 2 }}>
          Error displaying Discord data
        </Alert>
      );
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      const data = new FormData();

      if (avatar) {
        data.append("avatar", avatar);
      }
      if (formData.username !== user.username) {
        data.append("username", formData.username);
      }
      if (formData.email !== user.email) {
        data.append("email", formData.email);
      }
      if (formData.discord_id !== user.discord_id) {
        data.append("discord_id", formData.discord_id);
      }

      const updatedUser = await pb.collection("users").update(user.id, data);

      // Refresh auth store with updated user data
      pb.authStore.save(pb.authStore.token, updatedUser);

      setSuccess("Profile updated successfully");
      // Reset avatar state after successful upload
      setAvatar(null);

      // Force a re-render by updating the avatar URL with a timestamp
      const avatarInput = document.querySelector('input[type="file"]');
      if (avatarInput) {
        avatarInput.value = "";
      }
    } catch (err) {
      console.error("Update error:", err);
      setError(err?.data?.message || "Failed to update profile");
    }
  };

  const handleAvatarChange = async (e) => {
    if (e.target.files?.[0]) {
      const file = e.target.files[0];
      setAvatar(file);

      try {
        const formData = new FormData();
        formData.append("avatar", file);

        const updatedUser = await pb
          .collection("users")
          .update(user.id, formData);
        pb.authStore.save(pb.authStore.token, updatedUser);
        notifyAvatarChange(); // Add this line

        setSuccess("Avatar updated successfully");
        setAvatar(null);

        const avatarInput = document.querySelector('input[type="file"]');
        if (avatarInput) {
          avatarInput.value = "";
        }
      } catch (err) {
        console.error("Avatar update error:", err);
        setError(err?.data?.message || "Failed to update avatar");
      }
    }
  };

  const handleDeleteAccount = async () => {
    try {
      // Delete the user account
      await pb.collection("users").delete(user.id);

      // Clear auth store and local storage
      pb.authStore.clear();
      localStorage.removeItem("pocketbase_auth");

      // Navigate to home page
      navigate("/");
    } catch (err) {
      console.error("Delete account error:", err);
      setError(err?.data?.message || "Failed to delete account");
      setDeleteDialogOpen(false);
    }
  };

  // Modified function to download user data as ZIP with CSV and JSON
  const handleDownloadData = async () => {
    try {
      // Import JSZip dynamically
      const JSZip = (await import("jszip")).default;
      const zip = new JSZip();

      // Collect user data for CSV
      const userData = {
        username: user.username || "",
        email: user.email || "",
        discord_id: user.discord_id || "",
        name: user.name || "",
        created: user.created || "",
        updated: user.updated || "",
        verified: user.verified ? "Yes" : "No",
        is_admin: user.is_admin ? "Yes" : "No",
      };

      // Convert to CSV
      const csvRows = [];

      // Add header row
      const headers = Object.keys(userData);
      csvRows.push(headers.join(","));

      // Add data row
      const values = headers.map((header) => {
        const value = userData[header] || "";
        // Escape quotes and wrap in quotes if contains comma
        const escaped = String(value).replace(/"/g, '""');
        return `"${escaped}"`;
      });
      csvRows.push(values.join(","));
      const csvString = csvRows.join("\n");

      // Add CSV to zip
      zip.file(`${user.username}_data.csv`, csvString);

      // Add Discord data JSON to zip if available
      if (user.discord_data) {
        const discordData =
          typeof user.discord_data === "string"
            ? user.discord_data
            : JSON.stringify(user.discord_data, null, 2);
        zip.file(`${user.username}_discord_data.json`, discordData);
      }

      // Generate and download the zip file
      const content = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(content);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `${user.username}_account_data.zip`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSuccess("Data downloaded successfully");
    } catch (err) {
      console.error("Download error:", err);
      setError("Failed to download data");
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mb: 4 }}>
      <Paper sx={{ p: 4, mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          Account Settings
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box
          component="form"
          onSubmit={handleProfileUpdate}
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2, mt: 2 }}>
            <Avatar
              src={
                user?.avatar
                  ? `${pb.baseUrl}/api/files/users/${user.id}/${
                      user.avatar
                    }?${new Date().getTime()}`
                  : null
              }
              sx={{ width: 100, height: 100 }}
              imgProps={{
                crossOrigin: "anonymous",
                referrerPolicy: "no-referrer",
              }}
            />
          </Box>

          <Button variant="outlined" component="label" sx={{ mb: 2 }}>
            Change Avatar
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleAvatarChange}
            />
          </Button>

          <TextField
            label="Username"
            value={formData.username}
            onChange={(e) =>
              setFormData({ ...formData, username: e.target.value })
            }
            fullWidth
            disabled
          />

          <TextField
            label="Email"
            value={formData.email}
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
            fullWidth
            type="email"
            disabled
          />

          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleDownloadData}
              fullWidth
            >
              Download My Data
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setDeleteDialogOpen(true)}
              fullWidth
            >
              Delete Account
            </Button>
          </Stack>

          {/* Add Discord data display here */}
          {formatDiscordData()}
        </Box>
      </Paper>

      {/* Delete Account Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Account Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete your account? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteAccount} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Account;
